import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Calculator, UserPlus, Search, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateManualWR = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trackings, setTrackings] = useState(['']);
  const [clients, setClients] = useState([]);
  const [searchClient, setSearchClient] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  
  // Client selection mode: 'existing' or 'new'
  const [clientMode, setClientMode] = useState('existing');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // New client registration data
  const [newClientData, setNewClientData] = useState({
    full_name: '',
    locker_number: '',
    email: '',
    phone: '',
    id_document: '',
    delivery_address: ''
  });
  const [creatingClient, setCreatingClient] = useState(false);
  
  const [formData, setFormData] = useState({
    wr_number: '',
    shipping_method: 'Aereo',
    package_type: 'Caja',
    agent: 'Ninguno',
    // Peso
    final_weight_lb: '',
    // Dimensiones individuales en pulgadas
    dim_largo: '',
    dim_ancho: '',
    dim_alto: '',
    // Calculados automáticamente
    volume_ft3: '',
    volumetric_weight: '',
    pmax: '',
    pieces: 1,
    instructions: '',
    notes: ''
  });

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${API}/admin/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClients(response.data);
        setFilteredClients(response.data);
      } catch (error) {
      }
    };
    fetchClients();
  }, [token]);

  // Filter clients based on search
  useEffect(() => {
    if (searchClient.trim() === '') {
      setFilteredClients(clients);
    } else {
      const search = searchClient.toLowerCase();
      setFilteredClients(clients.filter(c => 
        c.full_name?.toLowerCase().includes(search) ||
        c.locker_number?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search)
      ));
    }
  }, [searchClient, clients]);

  // Auto-calcular cuando cambien las dimensiones o el peso
  useEffect(() => {
    const largo = parseFloat(formData.dim_largo) || 0;
    const ancho = parseFloat(formData.dim_ancho) || 0;
    const alto = parseFloat(formData.dim_alto) || 0;
    const pesoReal = parseFloat(formData.final_weight_lb) || 0;

    if (largo > 0 && ancho > 0 && alto > 0) {
      // Volumen en ft³ = (L x W x H) / 1728
      const volumen = (largo * ancho * alto) / 1728;
      
      // Peso Volumétrico (Aéreo) = (L x W x H) / 166
      const pesoVol = (largo * ancho * alto) / 166;
      
      // PMax = el mayor entre peso real y peso volumétrico
      const pmax = Math.max(pesoReal, pesoVol);

      setFormData(prev => ({
        ...prev,
        volume_ft3: volumen.toFixed(2),
        volumetric_weight: pesoVol.toFixed(2),
        pmax: pmax.toFixed(2)
      }));
    }
  }, [formData.dim_largo, formData.dim_ancho, formData.dim_alto, formData.final_weight_lb]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNewClientChange = (e) => {
    setNewClientData({ ...newClientData, [e.target.name]: e.target.value });
  };

  const handleAddTracking = () => {
    setTrackings([...trackings, '']);
  };

  const handleRemoveTracking = (index) => {
    setTrackings(trackings.filter((_, i) => i !== index));
  };

  const handleTrackingChange = (index, value) => {
    const newTrackings = [...trackings];
    newTrackings[index] = value;
    setTrackings(newTrackings);
  };

  const handleSelectClient = (client) => {
    setSelectedClientId(client.id);
    setSelectedClient(client);
    setSearchClient('');
  };

  // Create new client and get their ID
  const handleCreateNewClient = async () => {
    if (!newClientData.full_name.trim()) {
      alert('El nombre del cliente es requerido');
      return null;
    }
    if (!newClientData.locker_number.trim()) {
      alert('El número de casillero es requerido');
      return null;
    }

    setCreatingClient(true);
    try {
      const response = await axios.post(`${API}/admin/clients/quick-register`, {
        full_name: newClientData.full_name,
        locker_number: newClientData.locker_number,
        email: newClientData.email || `${newClientData.locker_number.toLowerCase()}@temp.local`,
        phone: newClientData.phone,
        id_document: newClientData.id_document,
        delivery_address: newClientData.delivery_address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ Cliente "${newClientData.full_name}" creado con casillero ${response.data.locker_number}`);
      
      // Return the new client ID
      return response.data.client_id;
    } catch (error) {
      alert('Error al crear cliente: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let clientId = null;
    let clientName = '';

    // Validate client selection
    if (clientMode === 'existing') {
      if (!selectedClientId) {
        alert('⚠️ Debe seleccionar un cliente existente o registrar uno nuevo');
        return;
      }
      clientId = selectedClientId;
      clientName = selectedClient?.full_name || '';
    } else {
      // Create new client first
      clientId = await handleCreateNewClient();
      if (!clientId) return; // Creation failed
      clientName = newClientData.full_name;
    }

    setLoading(true);

    try {
      // Construir dimensiones en formato LxWxH
      const dimensionsIn = `${formData.dim_largo}x${formData.dim_ancho}x${formData.dim_alto}`;
      
      const wrData = {
        client_id: clientId,
        client_name: clientName,
        wr_number: formData.wr_number,
        shipping_method: formData.shipping_method,
        package_type: formData.package_type,
        agent: formData.agent,
        final_weight_lb: parseFloat(formData.final_weight_lb) || 0,
        final_dimensions_in: dimensionsIn,
        volume: parseFloat(formData.volume_ft3) || 0,
        volumetric_weight: parseFloat(formData.volumetric_weight) || 0,
        pmax: parseFloat(formData.pmax) || 0,
        pieces: parseInt(formData.pieces) || 1,
        instructions: formData.instructions,
        notes: formData.notes,
        tracking_numbers: trackings.filter(t => t.trim() !== '')
      };

      const response = await axios.post(`${API}/admin/warehouse-receipts/manual`, wrData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ ${response.data.message}\nWR: ${response.data.wr_number}\nCliente: ${clientName}`);
      
      // Reset form
      setFormData({
        wr_number: '',
        shipping_method: 'Aereo',
        package_type: 'Caja',
        agent: 'Ninguno',
        final_weight_lb: '',
        dim_largo: '',
        dim_ancho: '',
        dim_alto: '',
        volume_ft3: '',
        volumetric_weight: '',
        pmax: '',
        pieces: 1,
        instructions: '',
        notes: ''
      });
      setTrackings(['']);
      setSelectedClientId('');
      setSelectedClient(null);
      setNewClientData({
        full_name: '',
        locker_number: '',
        email: '',
        phone: '',
        id_document: '',
        delivery_address: ''
      });
      setClientMode('existing');
      
      // Refresh clients list
      const clientsResponse = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(clientsResponse.data);
      
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Crear Warehouse Receipt Manual</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* SECCIÓN DE CLIENTE - REQUERIDA */}
            <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-blue-800">
                <User size={20} />
                Cliente (Requerido) *
              </h3>
              
              {/* Selector de modo */}
              <div className="flex gap-4 mb-4">
                <Button
                  type="button"
                  variant={clientMode === 'existing' ? 'default' : 'outline'}
                  onClick={() => setClientMode('existing')}
                  className="gap-2"
                >
                  <Search size={16} />
                  Buscar Cliente Existente
                </Button>
                <Button
                  type="button"
                  variant={clientMode === 'new' ? 'default' : 'outline'}
                  onClick={() => setClientMode('new')}
                  className="gap-2"
                >
                  <UserPlus size={16} />
                  Registrar Cliente Nuevo
                </Button>
              </div>

              {/* Modo: Cliente Existente */}
              {clientMode === 'existing' && (
                <div className="space-y-3">
                  {selectedClient ? (
                    <div className="bg-green-100 p-3 rounded border border-green-300">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-green-800">✅ Cliente Seleccionado:</p>
                          <p className="text-green-700">{selectedClient.full_name}</p>
                          <p className="text-sm text-green-600">Casillero: {selectedClient.locker_number} | Email: {selectedClient.email}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedClient(null); setSelectedClientId(''); }}
                        >
                          Cambiar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label>Buscar por nombre, casillero o email</Label>
                        <Input
                          value={searchClient}
                          onChange={(e) => setSearchClient(e.target.value)}
                          placeholder="Escriba para buscar..."
                          className="mt-1"
                        />
                      </div>
                      
                      {filteredClients.length > 0 && searchClient && (
                        <div className="max-h-48 overflow-y-auto border rounded bg-white">
                          {filteredClients.slice(0, 10).map((client) => (
                            <div
                              key={client.id}
                              onClick={() => handleSelectClient(client)}
                              className="p-2 hover:bg-blue-100 cursor-pointer border-b last:border-b-0"
                            >
                              <p className="font-medium">{client.full_name}</p>
                              <p className="text-sm text-gray-600">
                                📦 {client.locker_number} | ✉️ {client.email}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {!selectedClient && (
                        <p className="text-sm text-orange-600">
                          ⚠️ Debe seleccionar un cliente para poder generar facturas posteriormente
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Modo: Cliente Nuevo */}
              {clientMode === 'new' && (
                <div className="space-y-3 bg-yellow-50 p-3 rounded border border-yellow-300">
                  <p className="text-sm text-yellow-800 font-medium">
                    📝 Registrar nuevo cliente - Se creará automáticamente al guardar el WR
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Nombre Completo *</Label>
                      <Input
                        name="full_name"
                        value={newClientData.full_name}
                        onChange={handleNewClientChange}
                        placeholder="Nombre del cliente"
                        required={clientMode === 'new'}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Número de Casillero *</Label>
                      <Input
                        name="locker_number"
                        value={newClientData.locker_number}
                        onChange={handleNewClientChange}
                        placeholder="Ej: CAS001"
                        required={clientMode === 'new'}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Debe ser único en el sistema</p>
                    </div>
                    <div>
                      <Label className="text-sm">Email</Label>
                      <Input
                        name="email"
                        type="email"
                        value={newClientData.email}
                        onChange={handleNewClientChange}
                        placeholder="correo@ejemplo.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Teléfono</Label>
                      <Input
                        name="phone"
                        value={newClientData.phone}
                        onChange={handleNewClientChange}
                        placeholder="+58 412 1234567"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Cédula/Documento</Label>
                      <Input
                        name="id_document"
                        value={newClientData.id_document}
                        onChange={handleNewClientChange}
                        placeholder="V-12345678"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Dirección de Entrega</Label>
                      <Input
                        name="delivery_address"
                        value={newClientData.delivery_address}
                        onChange={handleNewClientChange}
                        placeholder="Dirección en Venezuela"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Información del WR */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3">Información del WR</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Número WR (auto si vacío)</Label>
                  <Input name="wr_number" value={formData.wr_number} onChange={handleChange} placeholder="WR20XXX" />
                </div>
                <div>
                  <Label>Método de Envío</Label>
                  <Select value={formData.shipping_method} onValueChange={(v) => setFormData({...formData, shipping_method: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aereo">Aéreo</SelectItem>
                      <SelectItem value="Maritimo">Marítimo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo de Paquete</Label>
                  <Select value={formData.package_type} onValueChange={(v) => setFormData({...formData, package_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Caja">Caja</SelectItem>
                      <SelectItem value="Sobre">Sobre</SelectItem>
                      <SelectItem value="Pallet">Pallet</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Agente</Label>
                  <Input name="agent" value={formData.agent} onChange={handleChange} />
                </div>
                <div>
                  <Label>Piezas</Label>
                  <Input name="pieces" type="number" value={formData.pieces} onChange={handleChange} min="1" />
                </div>
              </div>
            </div>

            {/* Medidas del Paquete Final - CON CALCULADORA */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Calculator size={20} />
                Paquete Final - Medidas (Cálculo Automático)
              </h3>
              
              {/* Peso */}
              <div className="mb-4">
                <Label>Peso Real (lb) *</Label>
                <Input 
                  name="final_weight_lb" 
                  type="number" 
                  step="0.01" 
                  value={formData.final_weight_lb} 
                  onChange={handleChange} 
                  placeholder="Ej: 15.5"
                  className="max-w-xs"
                  required 
                />
              </div>

              {/* Dimensiones Individuales */}
              <div className="mb-4">
                <Label className="mb-2 block">Dimensiones (pulgadas) *</Label>
                <div className="flex items-center gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">Largo (L)</Label>
                    <Input 
                      name="dim_largo" 
                      type="number" 
                      step="0.01" 
                      value={formData.dim_largo} 
                      onChange={handleChange} 
                      placeholder="18"
                      className="w-24"
                      required 
                    />
                  </div>
                  <span className="text-xl mt-5">×</span>
                  <div>
                    <Label className="text-xs text-gray-500">Ancho (W)</Label>
                    <Input 
                      name="dim_ancho" 
                      type="number" 
                      step="0.01" 
                      value={formData.dim_ancho} 
                      onChange={handleChange} 
                      placeholder="14"
                      className="w-24"
                      required 
                    />
                  </div>
                  <span className="text-xl mt-5">×</span>
                  <div>
                    <Label className="text-xs text-gray-500">Alto (H)</Label>
                    <Input 
                      name="dim_alto" 
                      type="number" 
                      step="0.01" 
                      value={formData.dim_alto} 
                      onChange={handleChange} 
                      placeholder="12"
                      className="w-24"
                      required 
                    />
                  </div>
                  <span className="text-gray-500 mt-5 ml-2">pulgadas</span>
                </div>
              </div>

              {/* Valores Calculados */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-3">📊 Valores Calculados Automáticamente</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-blue-700">Volumen (ft³)</Label>
                    <Input 
                      value={formData.volume_ft3} 
                      readOnly 
                      className="bg-white border-blue-300 font-bold text-blue-900"
                    />
                    <p className="text-xs text-blue-600 mt-1">= (L×W×H) / 1728</p>
                  </div>
                  <div>
                    <Label className="text-blue-700">P.Vol (lb) - Aéreo</Label>
                    <Input 
                      value={formData.volumetric_weight} 
                      readOnly 
                      className="bg-white border-blue-300 font-bold text-blue-900"
                    />
                    <p className="text-xs text-blue-600 mt-1">= (L×W×H) / 166</p>
                  </div>
                  <div>
                    <Label className="text-blue-700">PMax (lb)</Label>
                    <Input 
                      value={formData.pmax} 
                      readOnly 
                      className="bg-white border-blue-300 font-bold text-green-700"
                    />
                    <p className="text-xs text-blue-600 mt-1">= Mayor(Peso, PVol)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trackings Incluidos */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-lg mb-3">Trackings Incluidos (Opcional)</h3>
              <div className="space-y-2">
                {trackings.map((tracking, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={tracking}
                      onChange={(e) => handleTrackingChange(index, e.target.value)}
                      placeholder={`Tracking #${index + 1}`}
                    />
                    {trackings.length > 1 && (
                      <Button type="button" variant="outline" size="icon" onClick={() => handleRemoveTracking(index)}>
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={handleAddTracking} className="gap-2">
                  <Plus size={16} /> Agregar Tracking
                </Button>
              </div>
            </div>

            {/* Notas e Instrucciones */}
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Instrucciones Especiales</Label>
                  <Textarea name="instructions" value={formData.instructions} onChange={handleChange} rows={3} />
                </div>
                <div>
                  <Label>Notas Internas</Label>
                  <Textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} />
                </div>
              </div>
            </div>

            {/* Botón Submit */}
            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={loading || creatingClient} className="px-8">
                {loading || creatingClient ? 'Procesando...' : '✓ Crear Warehouse Receipt'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateManualWR;
